/**
 * PortalDocumentLibraryPage — Biblioteca de Documentos no Portal (E21.3.3, FR-7).
 *
 * O parceiro vê só os documentos da sua Audiência (resolvido server-side pelo
 * audience_service — a tela renderiza o que o backend liberou), filtra por
 * categoria e baixa via presigned URL (expira; o backend audita o acesso).
 * 4 estados: loading / erro / vazio / preenchido.
 */
import { useEffect, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  FileText,
  HardDrive,
  Loader2,
  Search,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import {
  usePortalDocuments,
  useConfirmReadDocument,
} from "@/hooks/usePortalCentralFeed";
import { portalCentralApi, type PortalDocument } from "@/services/portalApi";
import { triggerBrowserDownload } from "@/lib/browser-download";
import { PortalDocumentPreviewDialog } from "@/components/portal/PortalDocumentPreviewDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatBytes(n: number): string {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), u.length - 1);
  return `${(n / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
}

export default function PortalDocumentLibraryPage() {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const pageSize = 20;
  const { data, isLoading, isError, refetch, isFetching } = usePortalDocuments(
    page,
    pageSize,
    category.trim() || undefined
  );
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  // E21.3.4 — documento em preview (null = fechado).
  const [previewDoc, setPreviewDoc] = useState<PortalDocument | null>(null);
  // E21.3.5 — confirmação de Leitura Obrigatória.
  const confirm = useConfirmReadDocument();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);
  useEffect(() => {
    setPage(1);
  }, [category]);

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const info = await portalCentralApi.getDownloadInfo(id);
      triggerBrowserDownload(info.download_url);
    } catch {
      toast.error("Não foi possível baixar o documento.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-center gap-2">
        <FileText className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Documentos</h1>
          <p className="text-muted-foreground">
            Documentos disponibilizados para você. Baixe quando precisar.
          </p>
        </div>
      </div>

      <div className="relative mb-4 max-w-xs">
        <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="Filtrar por categoria…"
          className="pl-8"
          data-testid="portal-doc-category-filter"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="py-4">
                <Skeleton className="h-5 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar a Biblioteca</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            Não foi possível carregar os documentos.
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      ) : items.length === 0 ? (
        <div className="py-16 text-center" data-testid="portal-doc-empty">
          <FileText className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="text-muted-foreground">
            {category.trim()
              ? "Nenhum documento nessa categoria."
              : "Nenhum documento disponível por enquanto."}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3" data-testid="portal-doc-list">
            {items.map((doc) => (
              <Card key={doc.id} data-testid="portal-doc-card">
                <CardContent className="flex items-center justify-between gap-3 py-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-base font-semibold">{doc.title}</h2>
                      <Badge variant="secondary">{doc.category}</Badge>
                      <Badge variant="outline" className="gap-1">
                        {doc.source === "drive_ref" ? (
                          <HardDrive className="h-3 w-3" />
                        ) : (
                          <Upload className="h-3 w-3" />
                        )}
                        {doc.source === "drive_ref" ? "Drive" : "Arquivo"}
                      </Badge>
                      {doc.is_mandatory && (
                        <Badge
                          variant={doc.confirmed ? "outline" : "destructive"}
                          className="gap-1"
                          data-testid={`portal-doc-mandatory-${doc.id}`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Leitura obrigatória
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {doc.sector_name && <span>{doc.sector_name}</span>}
                      {doc.sector_name && <span>•</span>}
                      <span>{formatBytes(doc.size_bytes)}</span>
                      <span>•</span>
                      <span>{formatDate(doc.created_at)}</span>
                      {doc.is_mandatory && doc.confirmed && (
                        <>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1 font-medium text-green-600 dark:text-green-500">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Leitura confirmada
                          </span>
                        </>
                      )}
                      {doc.is_mandatory &&
                        !doc.confirmed &&
                        doc.mandatory_deadline && (
                          <>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              Prazo: {formatDate(doc.mandatory_deadline)}
                            </span>
                          </>
                        )}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {doc.is_mandatory && !doc.confirmed && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={confirm.isPending && confirm.variables === doc.id}
                        onClick={() => confirm.mutate(doc.id)}
                        data-testid={`portal-doc-confirm-${doc.id}`}
                      >
                        {confirm.isPending && confirm.variables === doc.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                        )}
                        Confirmar leitura
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewDoc(doc)}
                      data-testid={`portal-doc-preview-${doc.id}`}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Visualizar
                    </Button>
                    <Button
                      size="sm"
                      disabled={downloadingId === doc.id}
                      onClick={() => handleDownload(doc.id)}
                      data-testid={`portal-doc-download-${doc.id}`}
                    >
                      {downloadingId === doc.id ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      Baixar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Página {page} de {totalPages} ({total} documentos)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || isFetching}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {previewDoc && (
        <PortalDocumentPreviewDialog
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}
